import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SpecBottomSheet.css';

export interface SpecBottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showHandle?: boolean;
}

export default function SpecBottomSheet({
  open,
  onClose,
  children,
  title,
  showHandle = true,
}: SpecBottomSheetProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 250); // Match the longest animation duration (slideDown is 0.25s)
      
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender]);

  if (!shouldRender) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Only track touch start position, don't set isDragging yet
    // This prevents interfering with button clicks
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === null) return;

    const currentTouch = e.touches[0].clientY;
    const diff = currentTouch - touchStart;

    // Only allow dragging down (positive diff) and only start dragging after threshold
    if (diff > 10) {
      // Only set isDragging after user has actually moved enough
      if (!isDragging) {
        setIsDragging(true);
      }
      setTouchOffset(diff);
    }
  };

  const handleTouchEnd = () => {
    // If dragged down more than 100px, close the sheet
    if (isDragging && touchOffset > 100) {
      onClose();
    }

    // Reset
    setTouchStart(null);
    setTouchOffset(0);
    setIsDragging(false);
  };

  const content = (
    <div 
      className={`bottom-sheet-backdrop ${isClosing ? 'bottom-sheet-closing' : ''}`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`bottom-sheet-container ${isClosing ? 'bottom-sheet-closing' : ''} ${isDragging ? 'bottom-sheet-dragging' : ''}`}
        style={{
          transform: isDragging && touchOffset > 0 ? `translateY(${touchOffset}px)` : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle */}
        {showHandle && <div className="bottom-sheet-handle" />}
        
        {title && <div className="bottom-sheet-title">{title}</div>}
        
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level, outside any stacking contexts
  return createPortal(content, document.body);
}
